import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ProductTile from "@/components/store/ProductTile";
import CollaboratorsModal from "@/components/store/CollaboratorsModal";
import {
  X,
  Scale,
  ArrowUpRight,
  ShieldCheck,
  Sparkles,
  Building2,
  MapPin,
  Send,
  Check,
  Package,
  FolderKanban,
  Plus,
  Truck,
  Wrench,
  Share2,
  Copy,
  Users,
  Eye,
  UserPlus,
  Handshake,
} from "lucide-react";

/**
 * /store/compare — 2-4 product side-by-side comparison.
 * Reads product IDs from ?ids=A,B,C. Fetches via GET /store/compare.
 * Shows a factual attribute matrix + per-product long-form basis.
 */

export function StoreCompare() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareResult, setShareResult] = useState(null);
  const [shareLabel, setShareLabel] = useState("");
  const [shareNote, setShareNote] = useState("");
  const [shareEmail, setShareEmail] = useState(
    () => localStorage.getItem("mirrorloops_projects_email") || "",
  );

  const ids = useMemo(
    () => (params.get("ids") || "").split(",").filter(Boolean),
    [params],
  );

  useEffect(() => {
    if (ids.length < 2) return setData(null);
    (async () => {
      try {
        const r = await api.get(`/store/compare?ids=${ids.join(",")}`);
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Couldn't load comparison.");
      }
    })();
  }, [ids.join(",")]);

  const removeCol = (pid) => {
    const next = new URLSearchParams(params);
    next.set("ids", ids.filter((i) => i !== pid).join(","));
    setParams(next);
  };

  const createSnapshot = async () => {
    setShareBusy(true);
    try {
      const r = await api.post("/store/compare/snapshots", {
        product_ids: ids,
        label: shareLabel.trim() || null,
        note: shareNote.trim() || null,
        created_by_email: shareEmail.trim() || null,
      });
      setShareResult(r.data);
      try {
        await navigator.clipboard.writeText(r.data.share_url);
        toast.success("Share link copied to clipboard.");
      } catch {
        toast.success("Snapshot created — copy the link below.");
      }
      if (shareEmail.trim()) {
        localStorage.setItem("mirrorloops_projects_email", shareEmail.trim());
      }
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw : "Couldn't create snapshot.";
      toast.error(msg);
    } finally {
      setShareBusy(false);
    }
  };

  return (
    <PublicShell>
      <div data-testid="store-compare" className="max-w-7xl mx-auto px-6 pt-32 pb-24">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#E8D6A3] mb-2 flex items-center gap-2">
              <Scale size={12} /> Store · Compare
            </div>
            <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight text-white">
              Rack them side by side.
            </h1>
            <p className="mt-3 text-white/60 max-w-2xl">
              Every attribute Mirrorloops tracks — bright-lit for the ones that
              actually differ. Add up to 4 products from any search results page.
            </p>
          </div>
          {ids.length >= 2 && (
            <Button
              onClick={() => {
                setShareResult(null);
                setShareOpen(true);
              }}
              data-testid="compare-share-btn"
              className="rounded-full bg-[#D4B878] hover:bg-[#E8D6A3] text-black font-semibold whitespace-nowrap"
            >
              <Share2 size={13} className="mr-1.5" />
              Share this compare
            </Button>
          )}
        </div>

        {ids.length < 2 && (
          <div
            data-testid="store-compare-empty"
            className="p-10 border border-dashed border-white/10 rounded text-center text-white/50"
          >
            Add products to compare from the search results — the compare tray
            appears at the bottom of the screen.
          </div>
        )}

        {error && <div className="text-red-400 mb-4">{error}</div>}

        {data && data.products.length >= 2 && (
          <div className="overflow-x-auto" data-testid="store-compare-table">
            <div
              className="grid gap-3 min-w-[720px]"
              style={{
                gridTemplateColumns: `160px repeat(${data.products.length}, minmax(200px, 1fr))`,
              }}
            >
              {/* Header row — product tiles */}
              <div />
              {data.products.map((p) => (
                <div
                  key={p.id}
                  data-testid={`store-compare-col-${p.id}`}
                  className="relative border border-white/10 rounded-lg p-3 bg-white/[0.02]"
                >
                  <button
                    onClick={() => removeCol(p.id)}
                    data-testid={`store-compare-remove-${p.id}`}
                    className="absolute top-2 right-2 text-white/40 hover:text-white"
                  >
                    <X size={14} />
                  </button>
                  <div className="aspect-video mb-3">
                    <ProductTile p={p} size="md" />
                  </div>
                  <div className="text-[9px] uppercase tracking-[0.18em] text-[#D4B878] font-semibold">
                    {p.manufacturer}
                  </div>
                  <Link
                    to={`/store/product/${p.id}`}
                    className="block text-sm font-medium text-white hover:text-[#E8D6A3] mt-1 leading-tight line-clamp-2"
                  >
                    {p.name}
                  </Link>
                  <div className="text-[10px] text-white/40 mt-1 font-mono">{p.id}</div>
                </div>
              ))}

              {/* Attribute rows */}
              {data.matrix.map((row) => (
                <React.Fragment key={row.key}>
                  <div
                    className={`px-2 py-2 text-[10px] uppercase tracking-[0.18em] flex items-center ${
                      row.differs ? "text-[#E8D6A3]" : "text-white/40"
                    }`}
                  >
                    {row.label}
                  </div>
                  {row.values.map((v, i) => (
                    <div
                      key={`${row.key}-${i}`}
                      data-testid={`compare-cell-${row.key}-${i}`}
                      className={`px-3 py-2 text-xs rounded border ${
                        row.differs
                          ? "border-[#E8D6A3]/25 bg-[#E8D6A3]/[0.03] text-white"
                          : "border-white/10 bg-white/[0.01] text-white/70"
                      }`}
                    >
                      {v}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>

            {/* Long-form basis blocks */}
            <div
              className="mt-10 grid gap-4"
              style={{
                gridTemplateColumns: `repeat(${data.products.length}, minmax(240px, 1fr))`,
              }}
            >
              {data.long_form.map((lf, i) => (
                <div
                  key={lf.product_id}
                  data-testid={`compare-longform-${lf.product_id}`}
                  className="border border-white/10 rounded-lg p-4 bg-white/[0.02]"
                >
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] mb-2">
                    {data.products[i]?.manufacturer}
                  </div>
                  {lf.recommendation_basis && (
                    <div className="text-xs text-white/70 leading-relaxed mb-3">
                      {lf.recommendation_basis}
                    </div>
                  )}
                  {lf.mirrorloops_use_case && (
                    <div className="p-2 border border-[#CCFF00]/20 rounded text-[11px] text-white/80 mb-3">
                      <div className="text-[9px] uppercase tracking-[0.2em] text-[#CCFF00] mb-1">
                        Mirrorloops use case
                      </div>
                      {lf.mirrorloops_use_case}
                    </div>
                  )}
                  {lf.key_specs?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {lf.key_specs.map((k) => (
                        <span
                          key={k}
                          className="px-1.5 py-0.5 bg-white/[0.04] border border-white/10 rounded-full text-[9px] uppercase tracking-[0.14em] text-white/50 font-mono"
                        >
                          {k}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {shareOpen && (
          <div
            data-testid="compare-share-dialog"
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => !shareBusy && setShareOpen(false)}
          >
            <div
              className="bg-[#141110] border border-white/15 rounded-lg max-w-lg w-full p-6 space-y-4"
              onClick={(e) => e.stopPropagation()}
            >
              {shareResult ? (
                <div data-testid="compare-share-result">
                  <div className="flex items-center gap-2 mb-2">
                    <Check size={18} className="text-[#CCFF00]" />
                    <div className="text-white font-semibold">Shareable link ready</div>
                  </div>
                  <div className="text-xs text-white/60 mb-3">
                    Anyone with this link can view your comparison — no login needed. Send it to a client, embed it in a proposal, or park it in a Slack thread.
                  </div>
                  <div className="p-3 bg-[#0a0908] border border-[#D4B878]/30 rounded flex items-center gap-2">
                    <input
                      data-testid="compare-share-url"
                      value={shareResult.share_url}
                      readOnly
                      className="flex-1 bg-transparent border-0 outline-none text-xs text-[#E8D6A3] font-mono"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(shareResult.share_url);
                        toast.success("Copied.");
                      }}
                      data-testid="compare-share-copy"
                      className="text-white/60 hover:text-white p-1"
                    >
                      <Copy size={13} />
                    </button>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Link
                      to={`/store/compare/s/${shareResult.snapshot_id}`}
                      target="_blank"
                      data-testid="compare-share-open"
                      className="flex-1 rounded-full border border-white/20 text-white hover:bg-white/5 px-4 py-2 text-sm text-center flex items-center justify-center gap-1.5"
                    >
                      Open snapshot <ArrowUpRight size={12} />
                    </Link>
                    <Button
                      onClick={() => setShareOpen(false)}
                      data-testid="compare-share-close"
                      className="rounded-full bg-[#D4B878] text-black"
                    >
                      Done
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] font-semibold mb-1 flex items-center gap-1.5">
                      <Share2 size={11} /> Freeze this comparison
                    </div>
                    <div className="text-lg font-semibold text-white">
                      Turn this rack into a permalink
                    </div>
                    <div className="text-xs text-white/60 mt-1">
                      We snapshot every attribute right now — even if catalog data changes later, your client sees exactly what you sent.
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                      Label (optional)
                    </Label>
                    <Input
                      data-testid="compare-share-label"
                      value={shareLabel}
                      onChange={(e) => setShareLabel(e.target.value)}
                      placeholder="e.g. Q1 rollout shortlist for Cafe Locus"
                      className="bg-white/5 border-white/10 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                      Note to the recipient (optional)
                    </Label>
                    <textarea
                      data-testid="compare-share-note"
                      rows={2}
                      value={shareNote}
                      onChange={(e) => setShareNote(e.target.value)}
                      placeholder="Any context you want them to read at the top of the page"
                      className="w-full mt-1 bg-white/5 border border-white/10 rounded p-2 text-sm text-white placeholder:text-white/30 resize-none"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                      Shared by (optional email)
                    </Label>
                    <Input
                      data-testid="compare-share-email"
                      value={shareEmail}
                      onChange={(e) => setShareEmail(e.target.value)}
                      placeholder="you@agency.com"
                      className="bg-white/5 border-white/10 mt-1"
                    />
                  </div>
                  <Button
                    onClick={createSnapshot}
                    disabled={shareBusy}
                    data-testid="compare-share-create"
                    className="w-full rounded-full bg-[#CCFF00] hover:bg-[#B8E600] text-black font-semibold"
                  >
                    <Share2 size={13} className="mr-1.5" />
                    {shareBusy ? "Creating…" : "Generate share link"}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </PublicShell>
  );
}

const STATUS_META = {
  planning: { label: "Planning", tone: "text-white/60 border-white/15 bg-white/[0.03]" },
  ordered: { label: "Ordered", tone: "text-[#E8D6A3] border-[#E8D6A3]/40 bg-[#E8D6A3]/[0.06]" },
  installing: { label: "Installing", tone: "text-[#CCFF00] border-[#CCFF00]/40 bg-[#CCFF00]/[0.06]" },
  live: { label: "Live", tone: "text-[#00E5FF] border-[#00E5FF]/40 bg-[#00E5FF]/[0.06]" },
  on_hold: { label: "On hold", tone: "text-red-300 border-red-400/30 bg-red-500/[0.05]" },
};

// ============================================================
// PROJECTS
// ============================================================

export function StoreProjects() {
  const [email, setEmail] = useState(
    () => localStorage.getItem("mirrorloops_projects_email") || "",
  );
  const [tab, setTab] = useState("mine"); // "mine" | "shared"
  const [items, setItems] = useState([]);
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (e = email) => {
    if (!e) return;
    setLoading(true);
    try {
      const [own, sh] = await Promise.all([
        api.get(`/store/projects?email=${encodeURIComponent(e)}`),
        api.get(`/store/projects/shared-with-me?email=${encodeURIComponent(e)}`),
      ]);
      setItems(own.data.items || []);
      setShared(sh.data.items || []);
      localStorage.setItem("mirrorloops_projects_email", e);
    } catch {
      toast.error("Couldn't load projects.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (email) load(email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shownItems = tab === "mine" ? items : shared;

  return (
    <PublicShell>
      <div data-testid="store-projects" className="max-w-5xl mx-auto px-6 pt-32 pb-24">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4B878] mb-2 flex items-center gap-2">
            <FolderKanban size={12} /> Store · Projects
          </div>
          <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight text-white">
            Your rollouts, in one place.
          </h1>
          <p className="mt-3 text-white/60 max-w-2xl">
            Every saved BOM can be promoted to a multi-location project.
            Track status per store, invite installers, hit go-live.
          </p>
        </div>

        <div className="flex gap-2 mb-6 max-w-md">
          <Input
            data-testid="projects-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter the email you used to save the BOM"
            className="bg-white/5 border-white/10"
          />
          <Button
            onClick={() => load()}
            disabled={!email || loading}
            data-testid="projects-email-load"
            className="rounded-full bg-[#D4B878] text-black"
          >
            Load
          </Button>
        </div>

        {/* Tabs: Mine | Shared with me */}
        <div className="flex gap-2 mb-4 border-b border-white/10">
          {[
            { key: "mine", label: "My projects", icon: FolderKanban, count: items.length },
            { key: "shared", label: "Shared with me", icon: Handshake, count: shared.length },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`projects-tab-${t.key}`}
              className={`text-xs uppercase tracking-[0.18em] font-mono px-3 py-2 border-b-2 transition ${
                tab === t.key
                  ? "border-[#D4B878] text-white"
                  : "border-transparent text-white/40 hover:text-white/70"
              }`}
            >
              <t.icon size={11} className="inline mr-1.5" />
              {t.label}
              <span className="ml-1.5 text-[9px] text-white/40">({t.count})</span>
            </button>
          ))}
        </div>

        {shownItems.length === 0 && !loading && (
          <div
            data-testid={tab === "mine" ? "projects-empty" : "projects-shared-empty"}
            className="p-10 border border-dashed border-white/10 rounded text-center text-white/50"
          >
            {tab === "mine" ? (
              <>No projects yet. Head to <Link to="/store/build" className="text-[#D4B878] underline">BOM Builder</Link> to create one.</>
            ) : (
              <>Nothing shared with <span className="font-mono text-white/70">{email || "you"}</span> yet. Ask the project owner to invite you.</>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shownItems.map((p) => (
            <Link
              key={p.id}
              to={tab === "shared" ? `/store/projects/${p.id}?as=${encodeURIComponent(email)}` : `/store/projects/${p.id}`}
              data-testid={`project-card-${p.id}`}
              className="group border border-white/10 rounded-lg p-4 bg-white/[0.02] hover:border-[#D4B878]/40 transition-all"
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono">
                  {p.id.slice(0, 8)}
                </div>
                {tab === "shared" && (
                  <span
                    data-testid={`project-shared-role-${p.id}`}
                    className="text-[9px] uppercase tracking-[0.16em] font-mono px-1.5 py-0.5 rounded border border-[#00E5FF]/40 bg-[#00E5FF]/[0.06] text-[#00E5FF]"
                  >
                    {p._my_role || "viewer"}
                  </span>
                )}
              </div>
              <div className="text-lg font-semibold text-white leading-tight mb-2">
                {p.label}
              </div>
              <div className="flex items-center gap-4 text-xs text-white/50 mb-3 flex-wrap">
                <span className="flex items-center gap-1"><Building2 size={11} /> {p.locations?.length || 0} locations</span>
                {p.collaborators?.length > 0 && (
                  <span className="flex items-center gap-1"><Users size={11} /> {p.collaborators.length}</span>
                )}
                {p.bom_snapshot?.grand_total_estimate > 0 && (
                  <span className="font-mono">
                    {p.bom_snapshot.currency || "USD"} {p.bom_snapshot.grand_total_estimate.toLocaleString()}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1 pt-3 border-t border-white/10">
                {(p.locations || []).slice(0, 5).map((loc) => (
                  <span
                    key={loc.id}
                    className={`text-[9px] font-mono uppercase tracking-[0.12em] px-1.5 py-0.5 rounded border ${
                      STATUS_META[loc.status]?.tone || STATUS_META.planning.tone
                    }`}
                  >
                    {STATUS_META[loc.status]?.label || loc.status}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </PublicShell>
  );
}

export function StoreProjectDetail() {
  const { pid } = useParams();
  const [params] = useSearchParams();
  const asEmail = params.get("as") || "";
  const [proj, setProj] = useState(null);
  const [access, setAccess] = useState(null);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [collabOpen, setCollabOpen] = useState(false);
  const [newLoc, setNewLoc] = useState({
    name: "", address: "", country: "", installer_name: "", installer_email: "",
    target_go_live: "", status: "planning",
  });

  // Append ?as= to mutation URLs so the backend can enforce role-based edit access.
  const asQuery = asEmail ? `?as=${encodeURIComponent(asEmail)}` : "";

  const load = async () => {
    try {
      const [pr, acc] = await Promise.all([
        api.get(`/store/projects/${pid}`),
        api.get(`/store/projects/${pid}/access${asQuery}`),
      ]);
      setProj(pr.data);
      setAccess(acc.data);
    } catch (e) {
      const s = e?.response?.status;
      setError(
        s === 404 ? "Project not found." :
        s === 403 ? "You don't have access to this project." :
        "Couldn't load project.",
      );
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid, asEmail]);

  const canEdit = access?.can_edit ?? true;

  const addLoc = async () => {
    if (!newLoc.name) return toast.error("Name is required.");
    const payload = {};
    Object.entries(newLoc).forEach(([k, v]) => {
      if (typeof v === "string") {
        const trimmed = v.trim();
        if (trimmed) payload[k] = trimmed;
      } else if (v !== undefined && v !== null) {
        payload[k] = v;
      }
    });
    if (!payload.status) payload.status = "planning";
    try {
      await api.post(`/store/projects/${pid}/locations${asQuery}`, payload);
      toast.success("Location added.");
      setAddOpen(false);
      setNewLoc({
        name: "", address: "", country: "", installer_name: "",
        installer_email: "", target_go_live: "", status: "planning",
      });
      load();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      let msg = "Couldn't add location.";
      if (typeof raw === "string") {
        msg = raw;
      } else if (Array.isArray(raw) && raw.length > 0) {
        msg = raw
          .map((r) => (r?.msg ? `${(r.loc || []).slice(-1)[0] || "field"}: ${r.msg}` : String(r)))
          .join(" · ");
      }
      toast.error(msg);
    }
  };

  const updateStatus = async (locId, status) => {
    try {
      await api.patch(`/store/projects/${pid}/locations/${locId}${asQuery}`, { status });
      load();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "You have view-only access." : "Couldn't update status.");
    }
  };

  if (error) {
    return (
      <PublicShell>
        <div className="max-w-3xl mx-auto px-6 pt-32 pb-24 text-white/60">
          <div className="text-lg font-semibold text-red-300 mb-4">{error}</div>
          <Link to="/store/projects" className="text-[#D4B878] underline">← Back to Projects</Link>
        </div>
      </PublicShell>
    );
  }
  if (!proj) {
    return <PublicShell><div className="max-w-3xl mx-auto px-6 pt-32 pb-24 text-white/40">Loading…</div></PublicShell>;
  }

  return (
    <PublicShell>
      <div data-testid={`project-detail-${proj.id}`} className="max-w-5xl mx-auto px-6 pt-32 pb-24">
        <div className="text-[11px] text-white/40 mb-4 font-mono">
          <Link to="/store/projects" className="hover:text-white">Projects</Link>
          <span className="mx-2">/</span>
          <span className="text-white/70">{proj.id.slice(0, 8)}</span>
        </div>
        <div className="border border-[#D4B878]/25 rounded-lg p-6 mb-8 bg-gradient-to-br from-[#D4B878]/[0.05] to-transparent">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] mb-1 flex items-center gap-2">
                Project
                {access?.role && (
                  <span
                    data-testid="project-my-role"
                    className={`text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${
                      access.role === "owner"
                        ? "border-[#CCFF00]/40 bg-[#CCFF00]/[0.06] text-[#CCFF00]"
                        : access.role === "editor"
                        ? "border-[#00E5FF]/40 bg-[#00E5FF]/[0.06] text-[#00E5FF]"
                        : "border-white/20 bg-white/[0.03] text-white/70"
                    }`}
                  >
                    {access.role}
                  </span>
                )}
              </div>
              <h1 className="text-3xl font-semibold text-white">{proj.label}</h1>
              <div className="flex gap-4 text-xs text-white/60 mt-3 flex-wrap">
                <span><Building2 size={11} className="inline mr-1" />{proj.locations?.length || 0} locations</span>
                {proj.bom_snapshot?.grand_total_estimate > 0 && (
                  <span className="font-mono text-[#E8D6A3]">
                    {proj.bom_snapshot.currency || "USD"} {proj.bom_snapshot.grand_total_estimate.toLocaleString()}
                  </span>
                )}
                <span className="font-mono text-white/40">{proj.email}</span>
              </div>
            </div>
            {access?.role === "owner" && (
              <Button
                onClick={() => setCollabOpen(true)}
                data-testid="project-invite-btn"
                className="rounded-full bg-white/5 border border-white/15 hover:bg-white/10 text-white text-xs"
              >
                <UserPlus size={12} className="mr-1.5" />
                Invite collaborators
              </Button>
            )}
          </div>

          {/* Collaborators row */}
          {(proj.collaborators || []).length > 0 && (
            <div
              data-testid="project-collaborators-row"
              className="mt-4 pt-4 border-t border-white/10 flex items-center gap-2 flex-wrap"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono flex items-center gap-1">
                <Users size={11} /> Team
              </div>
              {(proj.collaborators || []).map((c) => (
                <span
                  key={c.id}
                  data-testid={`project-collab-badge-${c.email}`}
                  title={`${c.email} · ${c.role}`}
                  className="text-[10px] font-mono flex items-center gap-1.5 px-2 py-1 rounded-full border border-white/10 bg-white/[0.03]"
                >
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-semibold uppercase ${
                      c.role === "editor"
                        ? "bg-[#00E5FF]/20 text-[#00E5FF]"
                        : "bg-white/10 text-white/70"
                    }`}
                  >
                    {c.email.slice(0, 2)}
                  </span>
                  <span className="text-white/70 truncate max-w-[160px]">{c.email}</span>
                  <span className="text-white/40 uppercase tracking-[0.14em] text-[9px]">
                    {c.role}
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Locations</h2>
          {canEdit && (
            <Button
              onClick={() => setAddOpen(true)}
              data-testid="project-add-location-btn"
              className="rounded-full bg-[#CCFF00] text-black font-semibold"
            >
              <Plus size={14} className="mr-1" /> Add location
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
          {(proj.locations || []).length === 0 && (
            <div className="col-span-2 text-white/40 italic p-6 text-center border border-dashed border-white/10 rounded">
              No locations yet — {canEdit ? "add the first store to kick off the rollout." : "waiting on the owner to add locations."}
            </div>
          )}
          {(proj.locations || []).map((loc) => (
            <div
              key={loc.id}
              data-testid={`project-location-${loc.id}`}
              className="border border-white/10 rounded-lg p-4 bg-white/[0.02]"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="font-semibold text-white">{loc.name}</div>
                  {loc.address && <div className="text-xs text-white/50 mt-0.5">{loc.address}</div>}
                </div>
                <span
                  className={`text-[9px] font-mono uppercase tracking-[0.16em] px-2 py-0.5 rounded border ${STATUS_META[loc.status]?.tone}`}
                >
                  {STATUS_META[loc.status]?.label}
                </span>
              </div>
              {loc.installer_name && (
                <div className="text-[11px] text-white/60 flex items-center gap-1 mt-2">
                  <Wrench size={11} /> {loc.installer_name}
                </div>
              )}
              {loc.target_go_live && (
                <div className="text-[11px] text-white/60 flex items-center gap-1 mt-1">
                  <Truck size={11} /> Target: {loc.target_go_live}
                </div>
              )}
              {canEdit ? (
                <div className="flex gap-1 mt-3 flex-wrap">
                  {["planning", "ordered", "installing", "live", "on_hold"].map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(loc.id, s)}
                      data-testid={`project-loc-${loc.id}-status-${s}`}
                      className={`text-[9px] uppercase tracking-[0.14em] font-mono px-1.5 py-0.5 rounded border transition ${
                        loc.status === s
                          ? STATUS_META[s].tone
                          : "border-white/10 text-white/40 hover:text-white"
                      }`}
                    >
                      {STATUS_META[s].label}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-[10px] text-white/30 mt-3 italic flex items-center gap-1">
                  <Eye size={10} /> View-only
                </div>
              )}
            </div>
          ))}
        </div>

        {addOpen && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setAddOpen(false)} data-testid="project-add-dialog">
            <div className="bg-[#141110] border border-white/15 rounded-lg max-w-md w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
              <div className="text-lg font-semibold text-white mb-2">Add location</div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Name</Label>
                <Input data-testid="project-add-loc-name" value={newLoc.name} onChange={(e) => setNewLoc({ ...newLoc, name: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Address (optional)</Label>
                <Input data-testid="project-add-loc-address" value={newLoc.address} onChange={(e) => setNewLoc({ ...newLoc, address: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Installer</Label>
                  <Input data-testid="project-add-loc-installer" value={newLoc.installer_name} onChange={(e) => setNewLoc({ ...newLoc, installer_name: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Target go-live</Label>
                  <Input data-testid="project-add-loc-golive" value={newLoc.target_go_live} onChange={(e) => setNewLoc({ ...newLoc, target_go_live: e.target.value })} placeholder="e.g. 2026-04-15" className="bg-white/5 border-white/10 mt-1" />
                </div>
              </div>
              <Button onClick={addLoc} data-testid="project-add-loc-submit" className="w-full rounded-full bg-[#CCFF00] text-black font-semibold">
                <Plus size={13} className="mr-1" /> Add location
              </Button>
            </div>
          </div>
        )}

        {collabOpen && (
          <CollaboratorsModal
            projectId={pid}
            project={proj}
            onClose={() => setCollabOpen(false)}
            onUpdate={(collaborators) => {
              setProj((cur) => (cur ? { ...cur, collaborators } : cur));
            }}
          />
        )}
      </div>
    </PublicShell>
  );
}
